// src/services/backupUtils.js
import { Timestamp } from 'firebase/firestore';
import { BACKUP_CONFIG } from './backupConfig';

/**
 * Utilitaires pour le système de sauvegarde
 * Fonctions helper, formatage, validation, etc.
 */

// ================================
// FORMATAGE ET AFFICHAGE
// ================================

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const formatDuration = (milliseconds) => {
  if (milliseconds < 1000) return `${milliseconds}ms`;
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

export const formatBackupDate = (date, options = {}) => {
  if (!date) return 'Date inconnue';
  const dateObj = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
  const defaultOptions = {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Paris'
  };
  return dateObj.toLocaleString('fr-FR', { ...defaultOptions, ...options });
};

export const formatRelativeTime = (date) => {
  if (!date) return 'Jamais';
  const dateObj = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
  const now = new Date();
  const diffMs = now - dateObj;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffMinutes < 1) return 'À l\'instant';
  if (diffMinutes < 60) return `Il y a ${diffMinutes} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 30) return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
  return formatBackupDate(dateObj, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ================================
// GÉNÉRATION ET VALIDATION
// ================================

export const generateBackupId = (type = 'auto') => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `backup_${type}_${timestamp}_${random}`;
};

export const generateBackupName = (type, timestamp) => {
  const date = timestamp instanceof Date ? timestamp : timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const dateStr = date.toISOString().substring(0, 19).replace(/:/g, '-');
  const typeLabels = { 'auto': 'Auto', 'manual': 'Manuel', 'scheduled': 'Programmé', 'emergency': 'Urgence' };
  return `${typeLabels[type] || 'Backup'}_${dateStr}`;
};

export const validateBackupId = (backupId) => {
  if (!backupId || typeof backupId !== 'string') return { isValid: false, error: 'ID de backup manquant ou invalide' };
  const backupIdRegex = /^backup_[a-z]+_\d+_[a-z0-9]+$/;
  if (!backupIdRegex.test(backupId)) return { isValid: false, error: 'Format d\'ID de backup invalide' };
  return { isValid: true };
};

export const parseBackupId = (backupId) => {
  const validation = validateBackupId(backupId);
  if (!validation.isValid) return null;
  const parts = backupId.split('_');
  const timestamp = parseInt(parts[2]);
  return { type: parts[1], timestamp: new Date(timestamp), random: parts[3], original: backupId };
};

// ================================
// CALCULS ET STATISTIQUES
// ================================

export const calculateBackupSize = (backupData) => {
  if (!backupData || !backupData.collections) return 0;
  return JSON.stringify(backupData).length;
};

export const calculateBackupStats = (backups) => {
  if (!backups || backups.length === 0) {
    return { total: 0, successful: 0, failed: 0, successRate: 0, totalSize: 0, averageSize: 0, averageDuration: 0, lastBackup: null };
  }
  const successful = backups.filter(b => b.status === 'completed');
  const failed = backups.filter(b => b.status === 'failed');
  const totalSize = successful.reduce((sum, b) => sum + (b.metadata?.totalSize || 0), 0);
  const totalDuration = successful.reduce((sum, b) => sum + (b.metadata?.duration || 0), 0);
  return {
    total: backups.length,
    successful: successful.length,
    failed: failed.length,
    successRate: backups.length > 0 ? (successful.length / backups.length) * 100 : 0,
    totalSize,
    averageSize: successful.length > 0 ? totalSize / successful.length : 0,
    averageDuration: successful.length > 0 ? totalDuration / successful.length : 0,
    lastBackup: backups[0]
  };
};

// ================================
// FILTRAGE ET TRI
// ================================

export const filterBackups = (backups, filters = {}) => {
  if (!backups || backups.length === 0) return [];
  let filtered = [...backups];
  if (filters.status) filtered = filtered.filter(backup => backup.status === filters.status);
  if (filters.type) filtered = filtered.filter(backup => backup.type === filters.type);
  if (filters.dateFrom) {
    const fromDate = new Date(filters.dateFrom);
    filtered = filtered.filter(backup => {
      const backupDate = backup.timestamp instanceof Date ? backup.timestamp : backup.timestamp.toDate();
      return backupDate >= fromDate;
    });
  }
  if (filters.dateTo) {
    const toDate = new Date(filters.dateTo);
    filtered = filtered.filter(backup => {
      const backupDate = backup.timestamp instanceof Date ? backup.timestamp : backup.timestamp.toDate();
      return backupDate <= toDate;
    });
  }
  if (filters.minSize) filtered = filtered.filter(backup => (backup.metadata?.totalSize || 0) >= filters.minSize);
  return filtered;
};

export const sortBackups = (backups, sortBy = 'timestamp', order = 'desc') => {
  if (!backups || backups.length === 0) return [];
  
  const sorted = [...backups].sort((a, b) => {
    let valueA, valueB;
    
    switch (sortBy) {
      case 'timestamp':
        // Normaliser les timestamps en nombres
        valueA = typeof a.timestamp === 'number' ? a.timestamp : 
                 (a.timestamp?.toDate ? a.timestamp.toDate().getTime() : Date.now());
        valueB = typeof b.timestamp === 'number' ? b.timestamp : 
                 (b.timestamp?.toDate ? b.timestamp.toDate().getTime() : Date.now());
        break;
      case 'size':
        valueA = a.metadata?.totalSize || 0;
        valueB = b.metadata?.totalSize || 0;
        break;
      case 'duration':
        valueA = a.metadata?.duration || 0;
        valueB = b.metadata?.duration || 0;
        break;
      case 'documents':
        valueA = a.metadata?.totalDocuments || 0;
        valueB = b.metadata?.totalDocuments || 0;
        break;
      default:
        valueA = a[sortBy] || 0;
        valueB = b[sortBy] || 0;
    }
    
    return order === 'asc' ? (valueA > valueB ? 1 : -1) : (valueA < valueB ? 1 : -1);
  });
  
  return sorted;
};
// ================================
// VALIDATION ET VÉRIFICATION
// ================================

export const validateBackupIntegrity = (backupData) => {
  const errors = [];
  const warnings = [];
  if (!backupData) {
    errors.push('Données de backup manquantes');
    return { isValid: false, errors, warnings };
  }
  if (!backupData.id) errors.push('ID de backup manquant');
  if (!backupData.timestamp) errors.push('Timestamp manquant');
  if (!backupData.collections) errors.push('Collections manquantes');
  const criticalCollections = BACKUP_CONFIG.CRITICAL_COLLECTIONS;
  const backupCollections = Object.keys(backupData.collections || {});
  for (const collection of criticalCollections) {
    if (!backupCollections.includes(collection)) {
      errors.push(`Collection critique manquante: ${collection}`);
    } else if (backupData.collections[collection].error) {
      warnings.push(`Erreur dans la collection ${collection}: ${backupData.collections[collection].error}`);
    }
  }
  if (backupData.metadata) {
    if (backupData.status === 'completed' && !backupData.completedAt) warnings.push('Date de fin manquante pour un backup terminé');
    if (backupData.metadata.totalDocuments === 0) warnings.push('Aucun document dans le backup');
  }
  return { isValid: errors.length === 0, errors, warnings, score: Math.max(0, 100 - (errors.length * 25) - (warnings.length * 5)) };
};

export const canRestoreBackup = (backupData, userRole = null) => {
  const integrity = validateBackupIntegrity(backupData);
  if (!integrity.isValid) return { canRestore: false, reason: 'Backup corrompu ou invalide', details: integrity.errors };
  if (backupData.status !== 'completed') return { canRestore: false, reason: 'Backup non terminé', details: [`Statut actuel: ${backupData.status}`] };
  if (userRole && !BACKUP_CONFIG.SECURITY.PERMISSIONS.RESTORE_BACKUP.includes(userRole)) {
    return {
      canRestore: false,
      reason: 'Permissions insuffisantes',
      details: [`Rôle requis: ${BACKUP_CONFIG.SECURITY.PERMISSIONS.RESTORE_BACKUP.join(' ou ')}`]
    };
  }
  return { canRestore: true, reason: 'Backup prêt pour la restauration', details: [] };
};

// ================================
// UTILITAIRES DE NOTIFICATION
// ================================

export const formatNotificationMessage = (event, data = {}) => {
  const messages = {
    backup_success: `✅ Sauvegarde terminée avec succès`,
    backup_failure: `❌ Échec de la sauvegarde`,
    backup_warning: `⚠️ Avertissement lors de la sauvegarde`,
    restore_success: `✅ Restauration terminée avec succès`,
    restore_failure: `❌ Échec de la restauration`,
    storage_full: `💾 Espace de stockage insuffisant`,
    retention_cleanup: `🧹 Nettoyage automatique effectué`
  };
  let baseMessage = messages[event] || `Événement: ${event}`;
  if (data.backupId) baseMessage += `\nBackup: ${data.backupId}`;
  if (data.duration) baseMessage += `\nDurée: ${formatDuration(data.duration)}`;
  if (data.documentsCount) baseMessage += `\nDocuments: ${data.documentsCount.toLocaleString()}`;
  if (data.size) baseMessage += `\nTaille: ${formatFileSize(data.size)}`;
  if (data.error) baseMessage += `\nErreur: ${data.error}`;
  return baseMessage;
};

// ================================
// HELPERS DIVERS
// ================================

export const createTimestamp = (date = null) => Timestamp.fromDate(date || new Date());

export const normalizeDate = (dateInput) => {
  if (!dateInput) return new Date();
  if (dateInput instanceof Date) return dateInput;
  if (dateInput.toDate) return dateInput.toDate();
  return new Date(dateInput);
};

export const createOperationSummary = (operation, startTime, endTime, data = {}) => {
  const duration = endTime - startTime;
  return {
    operation,
    startTime: new Date(startTime),
    endTime: new Date(endTime),
    duration,
    durationFormatted: formatDuration(duration),
    success: data.success || false,
    error: data.error || null,
    details: data.details || {},
    timestamp: new Date()
  };
};

export const getSystemHealth = () => {
  return {
    status: 'ok',
    timestamp: new Date(),
    memoryUsage: '512MB',
    diskSpace: '120GB libre',
    cpuLoad: '15%'
  };
};

export default {
  formatFileSize,
  formatDuration,
  formatBackupDate,
  formatRelativeTime,
  generateBackupId,
  generateBackupName,
  validateBackupId,
  parseBackupId,
  calculateBackupSize,
  calculateBackupStats,
  filterBackups,
  sortBackups,
  validateBackupIntegrity,
  canRestoreBackup,
  formatNotificationMessage,
  createTimestamp,
  normalizeDate,
  createOperationSummary,
  getSystemHealth
};
